import { privateApi, publicApi } from '@/config';
import { sessionService } from '@/shared/services/session.service';
import { LoginCredentials, User, UserRole } from '@/shared/types';

// Tipos específicos para administradores
export interface AdminLoginResponse {
  user: User;
  token: string;
  refreshToken: string;
  permissions?: string[];
  role: UserRole;
}

export interface AdminUser extends User {
  role: UserRole;
  permissions?: string[];
  lastLoginAt?: string;
  isActive: boolean;
}

// API para administradores del sistema
export const adminApi = {
  // Login específico para administradores
  login: async (credentials: LoginCredentials): Promise<AdminLoginResponse> => {
    const response = await publicApi.post('/auth/admin/login', credentials);
    return response.data.data; // Extraer datos de la estructura { success: true, data: {...} }
  },

  // Verificar token de administrador
  verifyToken: async (): Promise<{ valid: boolean; user: AdminUser }> => {
    const response = await privateApi.get('/admin/auth/verify');
    return response.data.data; // Extraer datos de la estructura { success: true, data: {...} }
  },

  // Logout de administrador
  logout: async (): Promise<void> => {
    // GH#2691 — antes llamaba `POST /admin/auth/logout`, que el API NO tiene (404 medido en producción): la saga se traga el
    // fallo, así que el SUPER_ADMIN ve la sesión cerrada pero el servidor nunca se entera (cookies y refresh siguen vivos). La ruta
    // real es la del resto de roles, `POST /auth/logout`, con el refresh token en el cuerpo igual que `authApi.logout`.
    const refreshToken = sessionService.getRefreshToken();
    await privateApi.post('/auth/logout', { refreshToken });
  },

  // Refresh token de administrador
  refreshToken: async (
    refreshToken: string
  ): Promise<{ token: string; refreshToken: string }> => {
    const response = await privateApi.post('/admin/auth/refresh', {
      refreshToken,
    });
    return response.data.data; // Extraer datos de la estructura { success: true, data: {...} }
  },

  // Obtener perfil de administrador
  getProfile: async (): Promise<AdminUser> => {
    const response = await privateApi.get('/admin/profile');
    return response.data.data; // Extraer datos de la estructura { success: true, data: {...} }
  },

  // Actualizar perfil de administrador
  updateProfile: async (userData: Partial<AdminUser>): Promise<AdminUser> => {
    const response = await privateApi.put('/admin/profile', userData);
    return response.data.data; // Extraer datos de la estructura { success: true, data: {...} }
  },

  // Cambiar contraseña
  changePassword: async (passwordData: {
    currentPassword: string;
    newPassword: string;
  }): Promise<void> => {
    await privateApi.put('/admin/auth/change-password', passwordData);
  },

  // Obtener logs de actividad del administrador
  getActivityLogs: async (params?: {
    page?: number;
    limit?: number;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<{
    logs: any[];
    total: number;
    page: number;
    limit: number;
  }> => {
    const response = await privateApi.get('/admin/activity-logs', { params });
    return response.data.data; // Extraer datos de la estructura { success: true, data: {...} }
  },
};
